<template>
  <v-app class="widget-app">
    <v-container class="pa-3">
      <v-card variant="outlined">
        <v-card-title class="text-subtitle-1">
          Sagi Loyalty: {{ modeTitle }}
        </v-card-title>

        <v-card-text class="pb-2">
          <v-alert
            v-if="statusMessage"
            :type="statusType"
            variant="tonal"
            density="comfortable"
            class="mb-3"
          >
            {{ statusMessage }}
          </v-alert>

          <v-alert
            v-if="contextKey"
            type="info"
            variant="text"
            density="compact"
            class="mb-3"
          >
            contextKey: {{ contextKey }}
          </v-alert>

          <div v-if="!isOpenReceived" class="py-4">
            <v-progress-circular indeterminate size="20" class="mr-2" />
            Ожидание Open-события от МойСклад...
            <div v-if="isLocalDev" class="mt-3">
              <v-btn size="small" variant="tonal" @click="emitMockOpen">
                Сымитировать Open
              </v-btn>
            </div>
          </div>

          <template v-else>
            <v-alert
              v-if="documentState"
              type="info"
              variant="text"
              density="compact"
              class="mb-3"
            >
              Позиции отгрузки: {{ documentPositionsCount }}, сумма: {{ formatMoney(shipmentAmount) }}
            </v-alert>

            <v-row dense>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="phoneMasked"
                  label="Телефон клиента"
                  placeholder="7-777-033-69-16"
                  density="comfortable"
                  variant="outlined"
                  hide-details="auto"
                  :error-messages="phoneError ? [phoneError] : []"
                  @update:model-value="onPhoneInput"
                />
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center">
                <v-btn
                  block
                  color="primary"
                  :loading="isSearching"
                  :disabled="!canSearch"
                  @click="searchCustomer"
                >
                  Найти
                </v-btn>
              </v-col>
            </v-row>

          <v-card
            v-if="customer"
            class="mt-3"
            variant="tonal"
            color="grey-lighten-4"
          >
            <v-card-text class="py-3 widget-readable-text">
              <div><strong>Покупатель:</strong> {{ customer.fullName }}</div>
              <div><strong>Накоплено:</strong> {{ formatPoints(customer.totalPoints) }}</div>
              <div>
                <strong>Доступно к списанию:</strong>
                {{ formatPoints(customer.availablePoints) }}
                </div>
              </v-card-text>
            </v-card>

            <v-row v-if="customer" dense class="mt-2">
              <v-col cols="12" md="4">
                <v-select
                  v-model="operationType"
                  label="Операция"
                  :items="operationItems"
                  item-title="title"
                  item-value="value"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  @update:model-value="recalculateDiscount"
                />
              </v-col>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="operationAmountInput"
                  :label="operationLabel"
                  placeholder="0"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  :error-messages="amountError ? [amountError] : []"
                  @update:model-value="onOperationAmountInput"
                />
              </v-col>
            </v-row>

          <v-card
            v-if="discountCalculation"
            class="mt-3"
            variant="tonal"
            color="green-lighten-5"
          >
            <v-card-text class="py-3 widget-readable-text">
              <div>
                <strong>Сумма отгрузки:</strong>
                {{ formatMoney(discountCalculation.shipmentAmount) }}
              </div>
                <div>
                  <strong>Макс. списание:</strong>
                  {{ formatPoints(discountCalculation.maxWritablePoints) }}
                </div>
                <div>
                  <strong>Операция:</strong>
                  {{ operationType === "writeOff" ? "Списание" : "Начисление" }}
                </div>
                <div>
                  <strong>Сумма операции:</strong>
                  {{ formatPoints(discountCalculation.operationAmount) }}
                </div>
                <div>
                  <strong>Рекомендованная скидка:</strong>
                  {{ formatMoney(discountCalculation.recommendedDiscountAmount) }}
                </div>
                <div>
                  <strong>К оплате после скидки:</strong>
                  {{ formatMoney(discountCalculation.payableAmount) }}
                </div>
                <div>
                  <strong>Баллы после операции:</strong>
                  {{ formatPoints(discountCalculation.projectedAvailablePoints) }}
                </div>
              </v-card-text>
            </v-card>
          </template>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            v-if="isEditMode"
            size="small"
            color="primary"
            :loading="isUpdating"
            :disabled="!sdkReady || !canApplyToDocument"
            @click="pushUpdate"
          >
            Применить в документ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </v-app>
</template>

<script setup>
/* global defineProps */
import { computed, onMounted, ref } from "vue";
import axios from "axios";
import { loadWidgetSdk } from "@/widgets/loadWidgetSdk";

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: (value) => value === "create" || value === "edit",
  },
});

const CUSTOMER_LOOKUP_PATH = "/sagi/api/widget/customer-by-phone";
const DISCOUNT_CALC_PATH = "/sagi/api/widget/demand-discount";
const CONTEXT_PATH = "/sagi/api/widget/context";
const API_BASE = process.env.VUE_APP_SAGI_WIDGET_API_BASE || "";
const CUSTOMER_API_BASE =
  process.env.VUE_APP_SAGI_WIDGET_API_BASE || "https://api.dev.sagisoft.xyz";
const API_READY = !!API_BASE;
const ENV_MOCK_CONTEXT_KEY = process.env.VUE_APP_MOCK_CONTEXT_KEY || "";
const DEFAULT_LOCAL_MOCK_CONTEXT_KEY = "mock-context-key-local";

const operationItems = [
  { title: "Списать", value: "writeOff" },
  { title: "Начислить", value: "accrual" },
];
const isLocalDev =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

const isEditMode = computed(() => props.mode === "edit");
const modeTitle = computed(() =>
  isEditMode.value ? "Виджет отгрузки (редактирование)" : "Виджет отгрузки (создание)",
);
const operationLabel = computed(() =>
  operationType.value === "writeOff" ? "Сумма списания (баллы)" : "Сумма начисления (баллы)",
);

const sdkReady = ref(false);
const isUpdating = ref(false);
const isSearching = ref(false);
const isOpenReceived = ref(false);
const statusMessage = ref("");
const statusType = ref("info");
const lastChangeMessageId = ref(null);
const sdk = ref(null);
const openPayload = ref(null);
const documentState = ref(null);
const customer = ref(null);
const discountCalculation = ref(null);
const phoneMasked = ref("");
const phoneDigits = ref("");
const phoneError = ref("");
const operationType = ref("writeOff");
const operationAmountInput = ref("0");
const contextKey = ref("");
const isMockContextKey = ref(false);
const widgetContext = ref(null);

const canSearch = computed(() => phoneDigits.value.length === 11 && !isSearching.value);
const operationAmount = computed(() => {
  const normalized = operationAmountInput.value.replace(",", ".").trim();
  if (!normalized) {
    return 0;
  }
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return NaN;
  }
  return parsed;
});
const amountError = computed(() => {
  if (!customer.value) {
    return "";
  }
  if (Number.isNaN(operationAmount.value)) {
    return "Введите корректное число";
  }
  if (operationAmount.value < 0) {
    return "Сумма должна быть больше или равна 0";
  }
  if (
    operationType.value === "writeOff" &&
    discountCalculation.value &&
    operationAmount.value > discountCalculation.value.maxWritablePoints
  ) {
    return "Сумма списания больше доступного лимита";
  }
  return "";
});
const canApplyToDocument = computed(() => {
  return !!customer.value && !!discountCalculation.value && !amountError.value;
});
const currentDemand = computed(() => documentState.value || openPayload.value?.document || {});
const shipmentAmount = computed(() => getShipmentAmount(currentDemand.value));
const documentPositionsCount = computed(() => currentDemand.value?.positions?.rows?.length || currentDemand.value?.positions?.length || 0);

function setStatus(type, message) {
  statusType.value = type;
  statusMessage.value = message;
}

function extractContextKey() {
  const params = new URLSearchParams(window.location.search);
  const realContextKey = params.get("contextKey");
  if (realContextKey) {
    return { value: realContextKey, isMock: false };
  }

  const queryMockContextKey = params.get("mockContextKey");
  if (queryMockContextKey) {
    return { value: queryMockContextKey, isMock: true };
  }

  if (ENV_MOCK_CONTEXT_KEY) {
    return { value: ENV_MOCK_CONTEXT_KEY, isMock: true };
  }

  const isLocalhost =
    window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  if (isLocalhost) {
    return { value: DEFAULT_LOCAL_MOCK_CONTEXT_KEY, isMock: true };
  }

  return { value: "", isMock: false };
}

function formatPhoneMask(digits) {
  const clean = digits.replace(/\D/g, "").slice(0, 11);
  const p1 = clean.slice(0, 1);
  const p2 = clean.slice(1, 4);
  const p3 = clean.slice(4, 7);
  const p4 = clean.slice(7, 9);
  const p5 = clean.slice(9, 11);
  return [p1, p2, p3, p4, p5].filter(Boolean).join("-");
}

function normalizePhoneDigits(maskedValue) {
  const clean = maskedValue.replace(/\D/g, "");
  if (!clean.length) {
    return "";
  }
  if (clean[0] !== "7") {
    return `7${clean.slice(0, 10)}`;
  }
  return clean.slice(0, 11);
}

function getShipmentAmount(document) {
  if (!document) {
    return 0;
  }
  if (typeof document.sum === "number") {
    return document.sum / 100;
  }
  if (typeof document.payedSum === "number") {
    return document.payedSum / 100;
  }
  return 0;
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "KZT",
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatPoints(value) {
  return new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

async function loadContextByContextKey() {
  if (!contextKey.value) {
    setStatus("warning", "contextKey не передан в URL");
    return;
  }

  try {
    if (!API_READY) {
      widgetContext.value = {
        accountId: "mock-account",
        employeeId: "mock-employee",
      };
      setStatus("info", "Контекст загружен в mock-режиме");
      return;
    }

    const response = await axios.get(`${API_BASE}${CONTEXT_PATH}`, {
      params: { contextKey: contextKey.value },
    });
    widgetContext.value = response.data;
    setStatus("success", "Контекст пользователя загружен");
  } catch (error) {
    console.error("loadContextByContextKey failed", error);
    setStatus("error", "Не удалось загрузить контекст по contextKey");
  }
}

async function sendValidationFeedback(changeMessageId = null) {
  if (!sdk.value || !sdkReady.value) {
    return;
  }

  let errorMessage = "";

  if (!isOpenReceived.value) {
    errorMessage = "Ожидание открытия виджета";
  } else if (phoneError.value) {
    errorMessage = phoneError.value;
  } else if (!customer.value) {
    errorMessage = "Выберите клиента по телефону";
  } else if (amountError.value) {
    errorMessage = amountError.value;
  }

  try {
    await sdk.value.validationFeedback(
      !errorMessage,
      errorMessage || "Данные готовы к применению",
      changeMessageId,
    );
  } catch (error) {
    console.error("validationFeedback failed", error);
    setStatus("error", "Не удалось отправить validation feedback");
  }
}

function resetTransientState() {
  customer.value = null;
  discountCalculation.value = null;
  phoneMasked.value = "";
  phoneDigits.value = "";
  phoneError.value = "";
  operationType.value = "writeOff";
  operationAmountInput.value = "0";
}

function hydrateFromOpenPayload(payload) {
  openPayload.value = payload;
  documentState.value = payload?.objectState || payload?.document || null;
  isOpenReceived.value = true;
  resetTransientState();
}

function emitMockOpen() {
  hydrateFromOpenPayload({
    document: {
      id: "mock-demand-id",
      name: "MOCK-DEMAND-001",
      sum: 1500000,
      payedSum: 0,
      description: "Mock demand from local Open simulation",
    },
    context: widgetContext.value || {
      accountId: "mock-account",
      employeeId: "mock-employee",
    },
  });
  setStatus("info", "Open-событие сымитировано локально");
  sendValidationFeedback(lastChangeMessageId.value);
}

function buildLocalCalculation(customerData) {
  const demandAmount = shipmentAmount.value;
  const maxWritablePoints = Math.min(customerData.availablePoints || 0, demandAmount * 0.3);
  const safeOperationAmount = Number.isNaN(operationAmount.value) ? 0 : operationAmount.value;
  const clampedWriteOff = Math.min(safeOperationAmount, maxWritablePoints);

  if (operationType.value === "writeOff") {
    return {
      shipmentAmount: demandAmount,
      maxWritablePoints,
      operationAmount: clampedWriteOff,
      recommendedDiscountAmount: clampedWriteOff,
      payableAmount: Math.max(demandAmount - clampedWriteOff, 0),
      projectedAvailablePoints: Math.max((customerData.availablePoints || 0) - clampedWriteOff, 0),
    };
  }

  return {
    shipmentAmount: demandAmount,
    maxWritablePoints,
    operationAmount: safeOperationAmount,
    recommendedDiscountAmount: 0,
    payableAmount: demandAmount,
    projectedAvailablePoints: (customerData.availablePoints || 0) + safeOperationAmount,
  };
}

async function calculateDiscount(customerData) {
  if (!customerData) {
    discountCalculation.value = null;
    return;
  }

  if (!API_READY) {
    discountCalculation.value = buildLocalCalculation(customerData);
    return;
  }

  const response = await axios.post(
    `${API_BASE}${DISCOUNT_CALC_PATH}`,
    {
      contextKey: contextKey.value,
      customerId: customerData.id,
      demand: currentDemand.value,
      operation: operationType.value,
      amount: Number.isNaN(operationAmount.value) ? 0 : operationAmount.value,
    },
    { headers: { "Content-Type": "application/json" } },
  );

  discountCalculation.value = response.data;
}

async function searchCustomer() {
  if (!canSearch.value) {
    return;
  }

  isSearching.value = true;
  customer.value = null;
  discountCalculation.value = null;
  setStatus("info", "Идёт поиск клиента...");

  try {
    const response = await axios.post(
      `${CUSTOMER_API_BASE}${CUSTOMER_LOOKUP_PATH}`,
      {
        contextKey: contextKey.value,
        phone: phoneDigits.value,
      },
      { headers: { "Content-Type": "application/json" } },
    );

    customer.value = response.data;

    await calculateDiscount(customer.value);
    setStatus("success", "Клиент найден, расчёт выполнен");
  } catch (error) {
    console.error("searchCustomer failed", error);
    setStatus("error", "Клиент не найден или ошибка расчёта");
  } finally {
    isSearching.value = false;
    sendValidationFeedback(lastChangeMessageId.value);
  }
}

function onPhoneInput(value) {
  phoneDigits.value = normalizePhoneDigits(value);
  phoneMasked.value = formatPhoneMask(phoneDigits.value);
  customer.value = null;
  discountCalculation.value = null;

  if (!phoneDigits.value) {
    phoneError.value = "";
    sendValidationFeedback(lastChangeMessageId.value);
    return;
  }

  phoneError.value =
    phoneDigits.value.length < 11 ? "Введите телефон полностью в формате 7-777-033-69-16" : "";

  sendValidationFeedback(lastChangeMessageId.value);

  if (phoneDigits.value.length === 11) {
    searchCustomer();
  }
}

function onOperationAmountInput(value) {
  operationAmountInput.value = value.replace(",", ".");
  recalculateDiscount();
}

function recalculateDiscount() {
  if (!customer.value) {
    return;
  }
  calculateDiscount(customer.value)
    .catch((error) => {
      console.error("recalculateDiscount failed", error);
      setStatus("error", "Не удалось пересчитать скидку");
    })
    .finally(() => {
      sendValidationFeedback(lastChangeMessageId.value);
    });
}

function buildUpdateState() {
  if (!discountCalculation.value || !customer.value) {
    return {};
  }

  return {
    name: currentDemand.value?.name || "Demand",
    description: `Клиент: ${customer.value.fullName}. Операция: ${operationType.value}. Сумма: ${discountCalculation.value.operationAmount}`,
    attributes: {
      customerPhone: phoneDigits.value,
      customerName: customer.value.fullName,
      operationType: operationType.value,
      operationAmount: discountCalculation.value.operationAmount,
      availablePoints: customer.value.availablePoints,
      projectedAvailablePoints: discountCalculation.value.projectedAvailablePoints,
      discountAmount: discountCalculation.value.recommendedDiscountAmount,
      payableAmount: discountCalculation.value.payableAmount,
    },
  };
}

async function pushUpdate() {
  if (!sdk.value || !sdkReady.value || !isEditMode.value) {
    return;
  }

  if (!canApplyToDocument.value) {
    await sendValidationFeedback(lastChangeMessageId.value);
    return;
  }

  isUpdating.value = true;
  try {
    await sdk.value.update(buildUpdateState());
    setStatus("success", "Изменения применены в документ");
  } catch (error) {
    console.error("update failed", error);
    setStatus("error", "Не удалось применить изменения");
  } finally {
    isUpdating.value = false;
  }
}

onMounted(async () => {
  const contextInfo = extractContextKey();
  contextKey.value = contextInfo.value;
  isMockContextKey.value = contextInfo.isMock;
  await loadContextByContextKey();

  if (isMockContextKey.value && contextKey.value) {
    setStatus("warning", `Используется mock contextKey: ${contextKey.value}`);
  }

  try {
    const WidgetSDK = await loadWidgetSdk();
    sdk.value = WidgetSDK.create({ debug: true });
    sdkReady.value = true;

    sdk.value.onOpen((payload) => {
      hydrateFromOpenPayload(payload);
      sendValidationFeedback();
    });

    sdk.value.onChange((changeRequest) => {
      lastChangeMessageId.value = changeRequest?.messageId || null;
      documentState.value = changeRequest?.objectState || null;

      if (customer.value) {
        recalculateDiscount();
      } else {
        sendValidationFeedback(lastChangeMessageId.value);
      }
    });

    if (isLocalDev && !isOpenReceived.value) {
      setTimeout(() => {
        if (!isOpenReceived.value) {
          emitMockOpen();
        }
      }, 300);
    }
  } catch (error) {
    console.error("SDK init failed", error);
    setStatus("error", "Не удалось инициализировать SDK");
  }
});
</script>

<style scoped>
.widget-app {
  height: 100%;
  min-height: 100%;
  overflow-y: auto;
}

:global(html),
:global(body),
:global(#app) {
  height: 100%;
  margin: 0;
  overflow-y: auto;
}

.widget-readable-text {
  color: #000000;
}

.widget-readable-text strong {
  color: #000000;
}
</style>
